import { z } from 'zod';
import { uuidSchema, nameSchema, notesSchema, paginationSchema } from './common.js';

// ============================================================================
// PRINTER LIBRARY (Product Catalog)
// ============================================================================

export const createPrinterLibrarySchema = z.object({
  name: nameSchema,
  brandId: uuidSchema.nullable().optional(),
  modelId: uuidSchema.nullable().optional(),
  buildVolumeX: z.number().int().positive().max(2000).nullable().optional(),
  buildVolumeY: z.number().int().positive().max(2000).nullable().optional(),
  buildVolumeZ: z.number().int().positive().max(2000).nullable().optional(),
  nozzleTempMax: z.number().int().positive().max(500).nullable().optional(),
  bedTempMax: z.number().int().positive().max(200).nullable().optional(),
  chamberTempMax: z.number().int().positive().max(200).nullable().optional(),
  maxPrintSpeed: z.number().int().positive().max(1000).nullable().optional(),
  maxTravelSpeed: z.number().int().positive().max(1000).nullable().optional(),
  // Layer Resolution (Resin & Powder)
  zLayerHeightMin: z.number().int().positive().max(1000).nullable().optional(), // microns
  zLayerHeightMax: z.number().int().positive().max(1000).nullable().optional(), // microns
  // Resin Printer Specs
  xyResolution: z.number().int().positive().max(1000).nullable().optional(), // microns
  screenResolution: z.string().trim().max(50).nullable().optional(),
  lightSourceType: z.string().trim().max(50).nullable().optional(),
  lightWavelength: z.number().int().positive().max(1000).nullable().optional(), // nm
  liftSpeed: z.number().int().positive().max(10000).nullable().optional(), // mm/min
  resinVatCapacity: z.number().int().positive().max(100000).nullable().optional(), // ml
  // SLS/MJF Specs
  laserPower: z.number().int().positive().max(1000).nullable().optional(), // W
  laserSpotSize: z.number().int().positive().max(1000).nullable().optional(), // microns
  powderCapacity: z.number().positive().max(1000).nullable().optional(), // liters
  cooldownTime: z.number().int().positive().max(1000).nullable().optional(), // hours
  // Slots
  defaultNozzleSlots: z.number().int().positive().max(10).default(1),
  defaultHotendSlots: z.number().int().positive().max(10).default(1),
  defaultSpoolSlots: z.number().int().positive().max(20).default(1),
  defaultAccessorySlots: z.number().int().min(0).max(10).default(0),
  technology: z.enum(['FDM', 'SLA', 'SLS', 'MJF', 'BINDER_JET']).default('FDM'),
  notes: notesSchema,
});

export type CreatePrinterLibraryInput = z.infer<typeof createPrinterLibrarySchema>;

export const updatePrinterLibrarySchema = createPrinterLibrarySchema.partial();
export type UpdatePrinterLibraryInput = z.infer<typeof updatePrinterLibrarySchema>;

export const printerLibraryQuerySchema = paginationSchema.extend({
  search: z.string().trim().optional(),
  brandId: uuidSchema.optional(),
  modelId: uuidSchema.optional(),
  technology: z.enum(['FDM', 'SLA', 'SLS', 'MJF', 'BINDER_JET']).optional(),
  sortBy: z.enum(['name', 'brand', 'model', 'createdAt']).optional().default('name'),
  sortOrder: z.enum(['asc', 'desc']).optional().default('asc'),
  includeSystem: z.coerce.boolean().optional().default(true),
  includeCustom: z.coerce.boolean().optional().default(true),
});

export type PrinterLibraryQuery = z.infer<typeof printerLibraryQuerySchema>;

// ============================================================================
// TENANT PRINTERS (Owned Instances)
// ============================================================================

export const addPrinterToLibrarySchema = z.object({
  printerId: uuidSchema,
  instanceName: z.string().trim().min(1).max(100).nullable().optional(),
  serialNumber: z.string().trim().max(100).nullable().optional(),
  purchaseDate: z.coerce.date().nullable().optional(),
  purchasePrice: z.number().positive().max(1000000).nullable().optional(),
  status: z.enum(['IN_SERVICE', 'IN_STORAGE', 'NEEDS_MAINTENANCE', 'OUT_OF_SERVICE', 'DISPOSED']).optional().default('IN_SERVICE'),
  storageLocationId: uuidSchema.nullable().optional(),
});

export type AddPrinterToLibraryInput = z.infer<typeof addPrinterToLibrarySchema>;

export const updateTenantPrinterSchema = z.object({
  instanceName: z.string().trim().min(1).max(100).nullable().optional(),
  serialNumber: z.string().trim().max(100).nullable().optional(),
  purchaseDate: z.coerce.date().nullable().optional(),
  purchasePrice: z.number().positive().max(1000000).nullable().optional(),
  firstServiceDate: z.coerce.date().nullable().optional(),
  lastServiceDate: z.coerce.date().nullable().optional(),
  status: z.enum(['IN_SERVICE', 'IN_STORAGE', 'NEEDS_MAINTENANCE', 'OUT_OF_SERVICE', 'DISPOSED']).optional(),
  storageLocationId: uuidSchema.nullable().optional(),
});

export type UpdateTenantPrinterInput = z.infer<typeof updateTenantPrinterSchema>;

export const tenantPrinterQuerySchema = paginationSchema.extend({
  search: z.string().trim().optional(),
  status: z.enum(['IN_SERVICE', 'IN_STORAGE', 'NEEDS_MAINTENANCE', 'OUT_OF_SERVICE', 'DISPOSED']).optional(),
  brandId: uuidSchema.optional(),
  modelId: uuidSchema.optional(),
  sortBy: z.enum(['addedAt', 'instanceName', 'brand', 'status']).optional().default('addedAt'),
  sortOrder: z.enum(['asc', 'desc']).optional().default('desc'),
});

export type TenantPrinterQuery = z.infer<typeof tenantPrinterQuerySchema>;

// ============================================================================
// ACCESSORIES
// ============================================================================

export const createAccessoryTypeSchema = z.object({
  name: nameSchema,
  category: z.enum(['FILAMENT_SYSTEM', 'ENCLOSURE', 'CAMERA', 'AIR_FILTRATION', 'LIGHTING', 'DRYER', 'OTHER']).default('OTHER'),
  addsNozzleSlots: z.number().int().min(0).max(10).default(0),
  addsHotendSlots: z.number().int().min(0).max(10).default(0),
  addsSpoolSlots: z.number().int().min(0).max(20).default(0),
  addsAccessorySlots: z.number().int().min(0).max(10).default(0),
  description: z.string().trim().max(500).nullable().optional(),
});

export type CreateAccessoryTypeInput = z.infer<typeof createAccessoryTypeSchema>;

export const addAccessoryToPrinterSchema = z.object({
  accessoryTypeId: uuidSchema,
  quantity: z.number().int().positive().max(10).default(1),
  installedDate: z.coerce.date().nullable().optional(),
  notes: notesSchema,
});

export type AddAccessoryToPrinterInput = z.infer<typeof addAccessoryToPrinterSchema>;

export const updateAccessorySchema = z.object({
  quantity: z.number().int().positive().max(10).optional(),
  installedDate: z.coerce.date().nullable().optional(),
  notes: notesSchema,
});

export type UpdateAccessoryInput = z.infer<typeof updateAccessorySchema>;

// ============================================================================
// NOTES
// ============================================================================

export const createPrinterNoteSchema = z.object({
  title: z.string().trim().max(100).nullable().optional(),
  content: z.string().trim().min(1).max(5000),
  isPinned: z.boolean().default(false),
});

export type CreatePrinterNoteInput = z.infer<typeof createPrinterNoteSchema>;

export const updatePrinterNoteSchema = createPrinterNoteSchema.partial();
export type UpdatePrinterNoteInput = z.infer<typeof updatePrinterNoteSchema>;

// ============================================================================
// PRINTS
// ============================================================================

export const createPrintSchema = z.object({
  tenantPrinterId: uuidSchema,
  name: nameSchema,
  fileName: z.string().trim().max(255).nullable().optional(),
  startedAt: z.coerce.date(),
  completedAt: z.coerce.date().nullable().optional(),
  printDuration: z.number().positive().max(1000).nullable().optional(), // hours
  filamentUsedGrams: z.number().positive().max(10000).nullable().optional(),
  status: z.enum(['IN_PROGRESS', 'COMPLETED', 'FAILED', 'CANCELLED']).default('IN_PROGRESS'),
  estimatedCost: z.number().positive().max(100000).nullable().optional(),
  actualCost: z.number().positive().max(100000).nullable().optional(),
  notes: notesSchema,
});

export type CreatePrintInput = z.infer<typeof createPrintSchema>;

export const updatePrintSchema = createPrintSchema.partial().omit({ tenantPrinterId: true });
export type UpdatePrintInput = z.infer<typeof updatePrintSchema>;

export const printQuerySchema = paginationSchema.extend({
  tenantPrinterId: uuidSchema.optional(),
  status: z.enum(['IN_PROGRESS', 'COMPLETED', 'FAILED', 'CANCELLED']).optional(),
  startDate: z.coerce.date().optional(),
  endDate: z.coerce.date().optional(),
  sortBy: z.enum(['startedAt', 'completedAt', 'name', 'printDuration']).optional().default('startedAt'),
  sortOrder: z.enum(['asc', 'desc']).optional().default('desc'),
});

export type PrintQuery = z.infer<typeof printQuerySchema>;

// ============================================================================
// PRINTER BRANDS & MODELS (Keep existing)
// ============================================================================

/**
 * Create a new printer brand
 */
export const createPrinterBrandSchema = z.object({
  name: nameSchema,
});

export type CreatePrinterBrandInput = z.infer<typeof createPrinterBrandSchema>;

/**
 * Create a new printer model
 */
export const createPrinterModelSchema = z.object({
  name: nameSchema,
  brandId: uuidSchema,
});

export type CreatePrinterModelInput = z.infer<typeof createPrinterModelSchema>;

/**
 * Query printer models
 */
export const printerModelQuerySchema = z.object({
  brandId: uuidSchema.optional(),
});

export type PrinterModelQuery = z.infer<typeof printerModelQuerySchema>;
